import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isLeapYear from 'dayjs/plugin/isLeapYear'
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import EnvService from "../env";

// Locales
import 'dayjs/locale/en';
import 'dayjs/locale/en-au';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-ie';
import 'dayjs/locale/en-il';
import 'dayjs/locale/en-in';
import 'dayjs/locale/en-nz';
import 'dayjs/locale/en-sg';
import 'dayjs/locale/en-tt';
import 'dayjs/locale/es';
import 'dayjs/locale/es-do';
import 'dayjs/locale/es-mx';
import 'dayjs/locale/es-pr';
import 'dayjs/locale/es-us';
import 'dayjs/locale/fr';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/fr-ch';

// DO NOT REMOVE THE FOLLOWING LINES
dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isLeapYear)
dayjs.locale(EnvService.defaultLocale);
dayjs.tz.setDefault(EnvService.apiTimezone);

/**
 * The Adapter for the Dayjs utilities.
 *
 * - Fixes the issue with the value of the adapter being a string from MUI's TextField component.
 */
class BizkeytechAdapterDayjs extends AdapterDayjs {
    private static MISSING_UTC_PLUGIN = [
        'Missing UTC plugin',
        'To be able to use UTC or timezones, you have to enable the `utc` plugin',
        'Find more information on https://mui.com/x/react-date-pickers/timezone/#day-js-and-utc'
    ].join('\n');

    private static MISSING_TIMEZONE_PLUGIN = [
        'Missing timezone plugin',
        'To be able to use timezones, you have to enable both the `utc` and the `timezone` plugin',
        'Find more information on https://mui.com/x/react-date-pickers/timezone/#day-js-and-timezone'
    ].join('\n');

    /**
     * Checks if the UTC plugin is enabled.
     */
    private _hasUTCPlugin = () => typeof dayjs.utc !== 'undefined';

    /**
     * Checks if the Timezone plugin is enabled.
     */
    private _hasTimezonePlugin = () => typeof dayjs.tz !== 'undefined';

    /**
     * Cleans the timezone string.
     * @param timezone
     */
    private _cleanTimezone = (timezone: string) => {
        switch (timezone) {
            case 'default': {
                return undefined;
            }
            case 'system': {
                return dayjs.tz.guess();
            }
            default: {
                return timezone;
            }
        }
    };

    /**@inheritdoc */
    public getTimezone: AdapterDayjs['getTimezone'] = (value) => {
        if (value == null || value as unknown as string === '') {
            return 'system';
        }
        // noinspection SuspiciousTypeOfGuard
        if (typeof value === 'string') {
            return 'system';
        }
        if (this._hasUTCPlugin() && value.isUTC()) {
            return 'UTC';
        }
        if (this._hasTimezonePlugin()) {
            let _value$$x: any;
            // @ts-ignore
            const zone = (_value$$x = value.$x) == null ? void 0 : _value$$x.$timezone;
            return zone != null ? zone : 'system';
        }
        return 'system';
    }

    /**@inheritdoc */
    public setTimezone: AdapterDayjs['setTimezone'] = (value, timezone) => {
        if (value == null || (value as unknown as string) === '') {
            return null as any;
        }
        if (this.getTimezone(value) === timezone) {
            return value;
        }
        if (timezone === 'UTC') {
            /* istanbul ignore next */
            if (!this._hasUTCPlugin()) {
                throw new Error(BizkeytechAdapterDayjs.MISSING_UTC_PLUGIN);
            }
            return value.utc();
        }

        // We know that we have the UTC plugin.
        // Otherwise, the value timezone would always equal "system".
        // And it would be caught by the first "if" of this method.
        if (timezone === 'system') {
            return value.local();
        }
        if (!this._hasTimezonePlugin()) {
            if (timezone === 'default') {
                return value;
            }

            /* istanbul ignore next */
            throw new Error(BizkeytechAdapterDayjs.MISSING_TIMEZONE_PLUGIN);
        }
        return dayjs.tz(value, this._cleanTimezone(timezone));
    }
}


/**
 * The Date Utilities of the application.
 */
class DateUtils {

    public static readonly defaultFormat = 'lll';
    public static readonly defaultFormatLong = 'LLL';

    /**
     * The format of the date with the offset.
     */
    public static readonly withoutOffsetFormat = "YYYY-MM-DDTHH:mm:ss"

    /**
     * The format of the Date.
     */
    public static readonly dateFormat: string = "YYYY-MM-DD";

    /**
     * The format of the Time.
     */
    public static readonly dateTimeFormat: string = "YYYY-MM-DD [  ] hh:mm A";

    /**
     * The format of the Time.
     */
    public static readonly timeFormat: string = "hh:mm A";

    /**
     * The format of the Date in the alternative format.
     */
    public static readonly dateFormatAlternative: string = 'DD MMMM YYYY';

    public static readonly customFormats = {
        customerOutstandingStatement: 'MMM DD, YYYY',
        lookupAnalyticsChart: 'MMM DD, YYYY',
        lostSalesAnalyticsChart: 'MMM DD, YYYY',
        exportEntriesDate: 'MMM DD, YYYY',
        dashboardWidgetDate: 'MMM DD, YYYY HH:mm A',
    }

    /**
     * The format of MUI components in regard to date and time.
     */
    public static readonly MUIFormat = {
        datePicker: 'YYYY-MM-DD',
        timePicker: 'HH:mm:ss',
        dateTimePicker: 'YYYY-MM-DD HH:mm:ss',
    }

    /**
     * The instance of the Date Utilities
     */
    public static readonly instance = dayjs;

    /**
     * The adaptor of the utilities specific for the MUI localizations
     */
    public static readonly adapter = BizkeytechAdapterDayjs;

    /**
     * The server timezone of the application.
     */
    public static get serverTimezone() {
        return EnvService.apiTimezone;
    }

    /**
     * The local timezone of the application.
     */
    public static get localTimezone() {
        return this.instance.tz.guess();
    }

    /**
     * Generates a date object in the local timezone.
     * @returns The current date in the local timezone.
     */
    public static localNow(): DateUtilsTypes.Instance {
        return this.instance().tz(this.localTimezone);
    }

    /**
     * Generates a date object in the server timezone.
     * @returns The current date in the server timezone.
     */
    public static serverNow(): DateUtilsTypes.Instance {
        return this.instance().tz(this.serverTimezone);
    }

    /**
     * Generates a date object in UTC.
     * @returns The current date in UTC.
     */
    public static UTCNow(): DateUtilsTypes.Instance {
        return this.instance().utc();
    }

    /**
     * Generates a date object in the server timezone.
     * @param date
     */
    public static server(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.tz(date, this.serverTimezone);
    }

    /**
     * Generates a date object in UTC.
     * @param date
     * @constructor
     */
    public static UTC(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.utc(date);
    }

    /**
     * Generates a date object in the local timezone.
     * @param date
     */
    public static local(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.tz(date, this.localTimezone);
    }


    /**
     * Generates the UTC equivalent of a time in the server timezone.
     * @param date The date string to convert.
     * @returns The date in UTC.
     */
    public static serverToUTC(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.tz(date, this.serverTimezone).utc();
    }

    /**
     * Converts a date in UTC to the server timezone.
     *
     * @param date          The date string in UTC.
     * @returns The date in the server timezone.
     */
    public static UTCToServer(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.utc(date).tz(this.serverTimezone);
    }

    /**
     * Converts a date in the server timezone (Vancouver) to the local timezone (Iran).
     * @param date The date string in the server timezone.
     * @returns The date in the local timezone.
     */
    public static serverToLocal(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.tz(date, this.serverTimezone).tz(this.localTimezone);
    }


    /**
     * Converts a date in utc to the local timezone.
     * @param date      The date string in the local timezone.
     * @constructor
     */
    public static UTCToLocal(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.utc(date).tz(this.localTimezone);
    }


    /**
     * Converts a date in the local timezone to UTC.
     * @param date
     */
    public static localToUTC(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.tz(date, this.localTimezone).utc();
    }

    /**
     * Fetches the current date in the local timezone and converts it to UTC.
     */
    public static localNowToUTC() {
        return this.localToUTC(this.localNow());
    }

    public static localToServer(date?: DateUtilsTypes.FormatObject): DateUtilsTypes.Instance {
        return this.instance.tz(date, this.localTimezone).tz(this.serverTimezone);
    }

    /**
     * Updates the locale of the Date Utilities.
     * @param locale
     */
    public static onLocaleChange(locale: string) {
        this.instance.locale(locale);
        dayjs.locale(locale);
    }

    /**
     * Initializes the Date Utilities.
     */
    public static run() {
        dayjs.extend(advancedFormat);
        dayjs.extend(isoWeek);
        dayjs.extend(weekOfYear);
        dayjs.extend(relativeTime);
        dayjs.extend(utc);
        dayjs.extend(timezone);
        dayjs.extend(duration);
        dayjs.extend(isSameOrAfter);
        dayjs.extend(isSameOrBefore);
        dayjs.extend(isLeapYear)
        dayjs.locale(EnvService.defaultLocale);
        dayjs.tz.setDefault(EnvService.apiTimezone);
    }
}


export namespace DateUtilsTypes {
    export type FormatObject = dayjs.ConfigType;
    export type OptionType = dayjs.OptionType;
    export type UnitTypeShort = dayjs.UnitTypeShort;
    export type UnitTypeLong = dayjs.UnitTypeLong;
    export type UnitTypeLongPlural = dayjs.UnitTypeLongPlural;
    export type UnitType = dayjs.UnitType;
    export type OpUnitType = dayjs.OpUnitType;
    export type QUnitType = dayjs.QUnitType;
    export type ManipulateType = dayjs.ManipulateType;
    export type PluginFunc = dayjs.PluginFunc;
    export type Instance = dayjs.Dayjs;
    export type InstanceTimezone = dayjs.DayjsTimezone;

}

export default DateUtils;

